import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
// `@github-ui/issue-viewer` still carries a Relay dependency, which this rule exists to contain.
// This route is the migration off it; the leaves that still reach for Relay are documented on
// DataRouterIssueViewer. Lift once they are unpicked -- github/issues#22378.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {DataRouterIssueViewer} from '@github-ui/issue-viewer/DataRouterIssueViewer'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY} from '@github-ui/issue-viewer/IssueViewerNavigation'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ISSUE_VIEWER_DEFAULT_CONFIG, type IssueDeletionConnection} from '@github-ui/issue-viewer/OptionConfig'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {applyPendingSuggestionOperation} from '@github-ui/issue-viewer/PendingSuggestionOperation'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {accountIssueOptionFromPathname, parseArtifactNumberParam} from '@github-ui/issue-viewer/Urls'
import {applyIssueViewerFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
// This entry point carries no Relay dependency of its own; the restriction is package-wide.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useTimelinePrefill} from '@github-ui/issue-viewer/TimelinePrefill'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {
  isCanonicalIssueViewerQueryKey,
  persistIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {useLocation, useNavigate, useParams, useSearchParams} from '@github-ui/react-router'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {StaleCacheBanner} from '@github-ui/stale-cache-banner'
import {Banner} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {DashboardIssueSidePanel} from '../components/DashboardIssueSidePanel'
import {useIssueViewerSidePanel} from '../hooks/use-issue-viewer-side-panel'
import {issueViewerRoute} from './issue-viewer-route'
import {IssueSeoSchema} from './IssueSeoSchema'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

/**
 * The issue viewer on the Data Router.
 *
 * Everything rendered here comes from the route's page query, including the first timeline window,
 * so the page is complete after SSR.
 */
export function IssueViewer() {
  const payload = useRouteQuery(issueViewerRoute, 'mainQuery')
  const linkedArtifactNumber = parseArtifactNumberParam(useParams<{artifactNumber: string}>().artifactNumber)
  const primaryIssue = payload.data?.data?.repository?.issue
  const issueId = primaryIssue?.id
  const primaryIdentifier = useMemo(
    () =>
      primaryIssue
        ? {
            owner: primaryIssue.repository.owner.login,
            repo: primaryIssue.repository.name,
            number: primaryIssue.number,
          }
        : undefined,
    [primaryIssue],
  )
  const persistPrimaryAfterSettlement = useCallback(
    async ({queryClient}: {queryClient: Parameters<typeof persistIssueViewerPrimary>[1]}) => {
      if (primaryIdentifier) await persistIssueViewerPrimary(primaryIdentifier, queryClient)
    },
    [primaryIdentifier],
  )
  const mainQueryTargets = useMemo(
    () => [
      {
        queryKey: payload.queryKey,
        ...(primaryIdentifier && isCanonicalIssueViewerQueryKey(payload.queryKey)
          ? {onDocumentSettled: persistPrimaryAfterSettlement}
          : {}),
      },
    ],
    [payload.queryKey, persistPrimaryAfterSettlement, primaryIdentifier],
  )
  const projectItemsConnections = useMemo(
    () => (issueId ? [{ownerId: issueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}] : []),
    [issueId],
  )
  // The header and sidebar render from this payload, not the secondary view, so live updates need
  // this key to reach them.
  const routeQueryKey = useRouteQueryKey(issueViewerRoute, 'mainQuery')
  const renderedPayload = useOptimisticData(payload.data, {
    connections: projectItemsConnections,
    targets: mainQueryTargets,
    updateEntityFieldItems: applyPendingSuggestionOperation,
    applyEntityFieldItem: applyIssueViewerFieldValue,
  })
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const [searchParams] = useSearchParams()
  // Older backends serve a positive `timeline_page` from the Relay viewer or omit the page marker;
  // without a matching page-aware payload, the timeline hook fetches the isolated window itself.
  // Parsed strictly rather than with `parseInt`, which would accept `-1` and the `3` in `3junk` and
  // forward them to the endpoint as page numbers.
  const timelinePageParam = Number(searchParams.get('timeline_page'))
  const timelinePage = Number.isSafeInteger(timelinePageParam) && timelinePageParam > 0 ? timelinePageParam : undefined
  const parentIssue = renderedPayload?.data?.repository?.issue
  const parentOwner = parentIssue?.repository.owner.login
  const parentRepo = parentIssue?.repository.name
  const parentNumber = parentIssue?.number
  const {showSubIssue, deletionErrorNotice, sidePanelProps} = useIssueViewerSidePanel({
    parentOwner,
    parentRepo,
    parentNumber,
  })

  // FORBIDDEN/SAML reloads the page; other errors throw to the boundary.
  const data = handleRepositoryDataError(renderedPayload)
  const repository = data?.repository
  const issue = repository?.issue
  const owner = issue?.repository.owner.login
  const repo = issue?.repository.name

  const optionConfig = useMemo(
    () => ({
      ...ISSUE_VIEWER_DEFAULT_CONFIG,
      navigate,
      navigateBack: () => {
        if (owner && repo) {
          navigate(`/${owner}/${repo}/issues`, {
            state: {[ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY]: pathname},
          })
        }
      },
      // The default config's `onClose: noop` is truthy and the header renders its close button on
      // that alone, so a full-page view must clear it or it shows a dead "Close panel" X.
      onClose: undefined,
      accountIssueOption: accountIssueOptionFromPathname(pathname),
      onSubIssueClick: (
        {owner: subOwner, repo: subRepo, number: subNumber}: DashboardIssueIdentifier,
        options?: {issueDeletionConnection?: IssueDeletionConnection},
      ) => showSubIssue({owner: subOwner, repo: subRepo, number: subNumber}, options?.issueDeletionConnection),
      showIssueCreateButton: true,
      scrollToTopOnClick: true,
      timelineEventBaseUrl: owner && repo ? `/${owner}/${repo}/issues` : undefined,
      // This is a full-page view, like `IssueDetail`. The container-query layout keys off a named
      // container the legacy `IssueViewer` wrapper establishes and this route does not mount, so
      // without this the sidebar lays out under the timeline instead of beside it.
      useViewportQueries: true,
    }),
    [navigate, owner, pathname, repo, showSubIssue],
  )

  // Deliberately the overlaid payload: the timeline renders from the cache, so an optimistic edit --
  // a pinned badge, a reaction -- reaches it by being written through here.
  const timelineIssueId = issue?.id
  const frontTimelineItems = issue?.timelineItems
  const backTimelineItems = issue?.backTimelineItems
  const timelineFrontIssue = useMemo(
    () =>
      timelineIssueId && frontTimelineItems ? {id: timelineIssueId, timelineItems: frontTimelineItems} : undefined,
    [timelineIssueId, frontTimelineItems],
  )
  const timelineBackIssue = useMemo(
    () => (timelineIssueId && backTimelineItems ? {id: timelineIssueId, timelineItems: backTimelineItems} : undefined),
    [timelineIssueId, backTimelineItems],
  )

  const issueNumber = issue?.number
  const timelineCoordinates = useMemo(
    () => (owner && repo && issueNumber !== undefined ? {owner, repo, number: issueNumber} : undefined),
    [owner, repo, issueNumber],
  )

  // Before the timeline renders, not after: a section that has already decided to fetch cannot be
  // talked out of it. Returns the cursor its newest window is pinned to.
  const initialBackAnchor = useTimelinePrefill({
    coordinates: timelineCoordinates,
    page: timelinePage,
    payloadPage: payload.data?.timelinePage,
    issue: timelineFrontIssue,
    backIssue: timelineBackIssue,
    payloadUpdatedAt: payload.dataUpdatedAt,
    feed: payload.data?.feed,
  })

  if (!data) return null

  // A repository that resolved without an issue reaches here -- rendering null would show a blank
  // page that looks like a successful load. `issues#show` 404s the cases we expect; this covers the
  // rest.
  if (!issue) throw new Error(`Issue missing from repository data for ${pathname}`)

  return (
    <BaseAnalyticsWrapper category="Issue Show" appName="issues">
      <StaleCacheBanner />
      <IssueSeoSchema
        title={issue.title}
        body={issue.body}
        url={issue.url}
        createdAt={issue.createdAt}
        author={issue.author?.login ? {login: issue.author.login, profileUrl: issue.author.profileUrl} : null}
      />
      {deletionErrorNotice && (
        <Banner
          variant="critical"
          title={deletionErrorNotice.title}
          hideTitle
          description={deletionErrorNotice.message}
          className="m-3"
        />
      )}
      <DataRouterIssueViewer
        key={issue.id}
        issue={issue}
        feed={payload.data.feed}
        initialBackAnchor={initialBackAnchor}
        viewer={data?.safeViewer ?? null}
        isRepoOwnerEnterpriseManaged={repository?.isOwnerEnterpriseManaged}
        userNameDisplayConfiguration={repository.owner.userNameDisplayConfiguration}
        routeQueryKey={routeQueryKey}
        timelinePage={timelinePage}
        linkedArtifactNumber={linkedArtifactNumber}
        optionConfig={optionConfig}
      />
      <DashboardIssueSidePanel {...sidePanelProps} />
    </BaseAnalyticsWrapper>
  )
}
